import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { Eye, EyeOff, Lock, User, Mail, Building, Phone, Globe, Award, Shield, CheckCircle, Check, X, ArrowLeft } from 'lucide-react'
import travelAgentService from '../services/travelAgentService'
import LoadingSpinner from '../components/LoadingSpinner'
import AxonallLogo from '../components/AxonallLogo'

const TravelAgentRegistrationPage = () => {
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingUser, setIsLoadingUser] = useState(false)
  const [userData, setUserData] = useState(null)
  const navigate = useNavigate()
  const { userId } = useParams()

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm()

  const password = watch('password')

  // Password strength checker
  const checkPasswordStrength = (password) => {
    if (!password) return { score: 0, level: 'none', requirements: [] }

    const requirements = []
    let score = 0

    // Length check
    if (password.length >= 8) {
      score += 1
      requirements.push({ text: 'At least 8 characters', met: true })
    } else {
      requirements.push({ text: 'At least 8 characters', met: false })
    }

    // Uppercase check
    if (/[A-Z]/.test(password)) {
      score += 1
      requirements.push({ text: 'One uppercase letter', met: true })
    } else {
      requirements.push({ text: 'One uppercase letter', met: false })
    }

    // Lowercase check
    if (/[a-z]/.test(password)) {
      score += 1
      requirements.push({ text: 'One lowercase letter', met: true })
    } else {
      requirements.push({ text: 'One lowercase letter', met: false })
    }

    // Number check
    if (/\d/.test(password)) {
      score += 1
      requirements.push({ text: 'One number', met: true })
    } else {
      requirements.push({ text: 'One number', met: false })
    }

    // Special character check
    if (/[@$!%*?&]/.test(password)) {
      score += 1
      requirements.push({ text: 'One special character (@$!%*?&)', met: true })
    } else {
      requirements.push({ text: 'One special character (@$!%*?&)', met: false })
    }

    let level = 'weak'
    if (score >= 4) level = 'strong'
    else if (score >= 3) level = 'medium'
    else if (score >= 2) level = 'weak'
    else level = 'very-weak'

    return { score, level, requirements }
  }

  const passwordStrength = checkPasswordStrength(password)

  // Fetch user data when component mounts
  useEffect(() => {
    if (userId) {
      fetchUserData()
    } else {
      // Redirect to home if no userId provided
      navigate('/')
    }
  }, [userId, navigate])

  const fetchUserData = async () => {
    setIsLoadingUser(true)
    try {
      const response = await travelAgentService.getTravelAgentById(userId)
      setUserData(response.data)
    } catch (error) {
      // Show user-friendly error message
      const errorMessage = error.message || 'Unable to load registration details'
      toast.error(errorMessage)

      // Redirect to home after a short delay
      setTimeout(() => {
        navigate('/')
      }, 2000)
    } finally {
      setIsLoadingUser(false)
    }
  }

  const onSubmit = async (data) => {
    if (data.password !== data.confirmPassword) {
      toast.error('Passwords do not match')
      return
    }

    setIsLoading(true)
    try {
      const response = await travelAgentService.completeRegistrationWithUserId(userId, data.password)

      toast.success(response.message)

      // Navigate to login page since email verification is removed
      navigate('/login')
    } catch (error) {
      toast.error(error.message || 'Registration failed')
    } finally {
      setIsLoading(false)
    }
  }

  // Show loading spinner while fetching user data
  if (isLoadingUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <LoadingSpinner size="lg" className="mb-4 text-gray-800" />
          <p className="typography-body text-lg">Loading registration details...</p>
          <p className="typography-body-sm text-gray-500 mt-2">Please wait while we prepare your account</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen relative">
      {/* Background Image */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('https://d1su8gdu4l1hio.cloudfront.net/origin+mvp+assets/IDK-REQ/pexels-mikhail-nilov-8319463.jpg')`
        }}
      />
      <div className="absolute inset-0 bg-black bg-opacity-70" />

      {/* Back Button */}
      <div className="absolute top-6 left-6 z-20">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-white hover:text-gray-200 transition-colors duration-200 bg-black bg-opacity-30 hover:bg-opacity-50 rounded-lg px-3 py-2"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          Back
        </button>
      </div>

      {/* Main Content */}
      <div className="relative z-10 min-h-screen flex items-center justify-center py-8 px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-5xl">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="mb-6">
              <h1 className="mb-3">
                <AxonallLogo size="text-4xl" className="text-white" />
              </h1>

              <p className="text-lg text-white opacity-90 font-poppinsLight">
                New Connected Dimension of Luxury Travel
              </p>
            </div>
            <h2 className="text-3xl font-AXONALLPrimeLight text-white mb-3 drop-shadow-lg">
              Complete Registration
            </h2>
            <p className="text-white text-lg opacity-90 drop-shadow-md font-poppinsLight">
              Set your password to access your travel advisor portal
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Agent Details Card */}
            <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-8 shadow-2xl">
              <div className="flex items-center mb-6">
                <div className="p-3 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full mr-4">
                  <User className="h-6 w-6 text-white" />
                </div>
                <h3 className="typography-card-title text-xl text-white">Your Details</h3>
              </div>

              {userData && (
                <div className="space-y-6">
                  {/* Essential Information */}
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                    <h4 className="typography-card-title text-blue-200 mb-4 flex items-center text-base drop-shadow-sm">
                      <User className="h-4 w-4 mr-2" />
                      Registration Details
                    </h4>
                    <div className="space-y-4">
                      <div>
                        <p className="text-white/80 text-sm font-medium">Full Name</p>
                        <p className="text-white font-semibold text-base drop-shadow-sm">{userData.name}</p>
                      </div>
                      <div>
                        <p className="text-white/80 text-sm font-medium">Email Address</p>
                        <p className="text-white font-semibold text-base flex items-center drop-shadow-sm">
                          <Mail className="h-4 w-4 mr-2 text-green-300" />
                          {userData.email}
                        </p>
                      </div>
                      <div>
                        <p className="text-white/80 text-sm font-medium">Business Name</p>
                        <p className="text-white font-semibold text-base flex items-center drop-shadow-sm">
                          <Building className="h-4 w-4 mr-2 text-blue-300" />
                          {userData.legalBusinessName}
                        </p>
                      </div>
                      <div>
                        <p className="text-white/80 text-sm font-medium">Application ID</p>
                        <p className="text-white font-semibold text-base drop-shadow-sm">{userData.applicationId}</p>
                      </div>
                    </div>
                  </div>

                  {/* Status Badge */}
                  <div className="bg-green-500/20 backdrop-blur-sm rounded-xl p-4 border border-green-400/30">
                    <div className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-green-300 mr-3" />
                      <div>
                        <p className="text-green-200 font-semibold text-base drop-shadow-sm">Application Approved</p>
                        <p className="text-green-100 text-sm drop-shadow-sm">Complete your registration to access the portal</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Registration Form */}
            <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-8 shadow-2xl">
              <div className="flex items-center mb-6">
                <div className="p-3 bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-full mr-4">
                  <Shield className="h-6 w-6 text-white" />
                </div>
                <h3 className="typography-card-title text-xl text-white">Create Password</h3>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {/* Password */}
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-white mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-white/60" />
                    </div>
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      className="w-full pl-10 pr-10 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 text-base"
                      placeholder="Enter your password"
                      {...register('password', {
                        required: 'Password is required',
                        minLength: {
                          value: 8,
                          message: 'Password must be at least 8 characters long'
                        },
                        pattern: {
                          value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
                          message: 'Password must meet all strength requirements'
                        },
                        validate: (value) => {
                          const strength = checkPasswordStrength(value)
                          if (strength.score < 4) {
                            return 'Password must meet at least 4 out of 5 strength requirements'
                          }
                          return true
                        }
                      })}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-white/60 hover:text-white transition-colors"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="mt-1 text-sm text-red-300">{errors.password.message}</p>
                  )}

                  {/* Password Strength Indicator */}
                  {password && (
                    <div className="mt-3 p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center">
                          <Shield className="h-4 w-4 text-white mr-2" />
                          <span className="text-sm font-semibold text-white drop-shadow-sm">Password Strength</span>
                        </div>
                        <div className={`px-3 py-1 rounded-full text-xs font-bold drop-shadow-sm ${passwordStrength.level === 'strong' ? 'bg-green-500/40 text-green-100' :
                            passwordStrength.level === 'medium' ? 'bg-yellow-500/40 text-yellow-100' :
                              passwordStrength.level === 'weak' ? 'bg-orange-500/40 text-orange-100' :
                                'bg-red-500/40 text-red-100'
                          }`}>
                          {passwordStrength.level === 'strong' ? 'Strong' :
                            passwordStrength.level === 'medium' ? 'Medium' :
                              passwordStrength.level === 'weak' ? 'Weak' :
                                'Very Weak'}
                        </div>
                      </div>

                      {/* Strength Bar */}
                      <div className="mb-3">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-xs text-white/90 font-medium drop-shadow-sm">Strength Level</span>
                          <span className="text-xs text-white/90 font-medium drop-shadow-sm">{passwordStrength.score}/5</span>
                        </div>
                        <div className="relative w-full bg-white/20 rounded-full h-3 overflow-hidden">
                          <div className={`h-3 rounded-full transition-all duration-500 ease-out ${passwordStrength.level === 'strong' ? 'bg-gradient-to-r from-green-400 via-green-500 to-green-600' :
                              passwordStrength.level === 'medium' ? 'bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600' :
                                passwordStrength.level === 'weak' ? 'bg-gradient-to-r from-orange-400 via-orange-500 to-orange-600' :
                                  'bg-gradient-to-r from-red-400 via-red-500 to-red-600'
                            }`} style={{ width: `${(passwordStrength.score / 5) * 100}%` }}>
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-pulse"></div>
                          </div>
                          <div className="absolute inset-0 flex justify-between px-1">
                            {[1, 2, 3, 4, 5].map((marker) => (
                              <div
                                key={marker}
                                className={`w-0.5 h-3 rounded-full transition-all duration-300 ${marker <= passwordStrength.score ? 'bg-white/50' : 'bg-white/20'
                                  }`}
                              ></div>
                            ))}
                          </div>
                        </div>
                        <div className="flex justify-between mt-1">
                          <span className="text-xs text-white/80 drop-shadow-sm">Weak</span>
                          <span className="text-xs text-white/80 drop-shadow-sm">Strong</span>
                        </div>
                      </div>

                      {/* Requirements List */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {passwordStrength.requirements.map((req, index) => (
                          <div key={index} className={`flex items-center p-2 rounded-lg transition-all duration-200 ${req.met ? 'bg-green-500/30 border border-green-400/40' : 'bg-red-500/30 border border-red-400/40'
                            }`}>
                            <div className={`p-1 rounded-full mr-3 ${req.met ? 'bg-green-500/50' : 'bg-red-500/50'
                              }`}>
                              {req.met ? (
                                <Check className="h-3 w-3 text-green-100" />
                              ) : (
                                <X className="h-3 w-3 text-red-100" />
                              )}
                            </div>
                            <span className={`text-xs font-medium drop-shadow-sm ${req.met ? 'text-green-100' : 'text-red-100'
                              }`}>
                              {req.text}
                            </span>
                          </div>
                        ))}
                      </div>

                      {/* Success Message */}
                      {passwordStrength.level === 'strong' && (
                        <div className="mt-3 p-2 bg-green-500/30 border border-green-400/40 rounded-lg">
                          <div className="flex items-center">
                            <CheckCircle className="h-4 w-4 text-green-200 mr-2" />
                            <span className="text-xs font-medium text-green-100 drop-shadow-sm">
                              Excellent! Your password meets all security requirements.
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Confirm Password */}
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-white mb-2">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-white/60" />
                    </div>
                    <input
                      id="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      className="w-full pl-10 pr-10 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 text-base"
                      placeholder="Confirm your password"
                      {...register('confirmPassword', {
                        required: 'Please confirm your password',
                        validate: value => value === password || 'Passwords do not match'
                      })}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-white/60 hover:text-white transition-colors"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <p className="mt-1 text-sm text-red-300">{errors.confirmPassword.message}</p>
                  )}
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-white hover:bg-gray-100 text-black font-semibold py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center text-base"
                >
                  {isLoading ? (
                    <>
                      <LoadingSpinner size="sm" className="mr-2" />
                      Completing Registration...
                    </>
                  ) : (
                    <>
                      <Award className="h-5 w-5 mr-2 text-yellow-600" />
                      Complete Registration
                    </>
                  )}
                </button>
              </form>

              {/* Login Link */}
              <div className="mt-6 text-center">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-white/20"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-transparent text-white/70">Already have an account?</span>
                  </div>
                </div>
                <p className="mt-3 text-white/80 text-base">
                  <button
                    onClick={() => navigate('/login')}
                    className="text-blue-300 hover:text-blue-200 font-medium transition-colors"
                  >
                    Sign in here
                  </button>
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="text-center mt-8">
            <p className="text-white/60 text-sm">
              © 2025 <AxonallLogo size="text-sm" className="text-white" />. All rights reserved.
            </p>
            <div className="flex justify-center space-x-4 mt-2 text-xs">
              <span className="text-white/50">Privacy Policy</span>
              <span className="text-white/50">Terms of Service</span>
              <span className="text-white/50">Support</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TravelAgentRegistrationPage
